import React, { useState } from 'react';
import { Images, attributeTypes } from 'utils';
import { Select, MenuItem, Input } from '@mui/material';
import { SelectTypeAutocomplete } from 'components';
import { goalGraphTypeEnums, goalGraphValueModeEnums, sessionGroupByEnums } from '../../attributes/constnats';
import { useFormBuilder } from '../../formContext';

const goalGraphDataTypeList = [
    { name: 'Cumulative', type: goalGraphTypeEnums.CUMULATIVE, id: goalGraphTypeEnums.CUMULATIVE },
    { name: 'Target Overview', type: goalGraphTypeEnums.TARGET_OVERVIEW, id: goalGraphTypeEnums.TARGET_OVERVIEW },
];

const goalGraphValueModeList = [
    { name: '%', id: goalGraphValueModeEnums.PERCENT },
    { name: 'Count', id: goalGraphValueModeEnums.COUNT },
];

/**
 * "% / Count" for a CUMULATIVE goal graph — the same switch as Cumulative Goal
 * Progress in Clinical Insights: percentage of the goal's targets mastered, or
 * the number of mastered targets. Stored as `properties.valueMode`; absent
 * means PERCENT.
 */
const ValueModeSwitch = ({ selectedItem, handleUpdate }) => {
    const valueMode = selectedItem?.properties?.valueMode || goalGraphValueModeEnums.PERCENT;
    return (
        <div className="chart-option-row">
            <span className="option-label">Values</span>
            <div className="chart-segmented-control">
                {goalGraphValueModeList.map((mode) => (
                    <button
                        key={mode.id}
                        type="button"
                        className={`chart-segmented-btn ${valueMode === mode.id ? 'active' : ''}`}
                        onClick={() => handleUpdate('valueMode', mode.id)}
                    >
                        {mode.name}
                    </button>
                ))}
            </div>
        </div>
    );
};

const sessionGroupByList = [
    { name: 'Auto', id: sessionGroupByEnums.AUTO },
    { name: 'Day', id: sessionGroupByEnums.DAY },
    { name: 'Week', id: sessionGroupByEnums.WEEK },
    { name: 'Month', id: sessionGroupByEnums.MONTH },
];

/**
 * How session data is grouped on a TARGET / Target Overview graph. Auto (stored absent or AUTO) goes by the
 * report period's length with the API / PDF rule: ≤31 days → day, ≤93 → week, else month. (The app used ≤120
 * for weeks before the grouping moved to the API, so 94–120-day reports are monthly now.) Stored as
 * `properties.sessionGroupBy`; the API does the grouping.
 */
const SessionGroupBySwitch = ({ selectedItem, handleUpdate }) => {
    const groupBy = selectedItem?.properties?.sessionGroupBy || sessionGroupByEnums.AUTO;
    return (
        <div className="chart-option-row">
            <span className="option-label">Group Sessions By</span>
            <div className="chart-segmented-control">
                {sessionGroupByList.map((option) => (
                    <button
                        key={option.id}
                        type="button"
                        className={`chart-segmented-btn ${groupBy === option.id ? 'active' : ''}`}
                        onClick={() => handleUpdate('sessionGroupBy', option.id)}
                    >
                        {option.name}
                    </button>
                ))}
            </div>
        </div>
    );
};

const SetupSection = ({ selectedItem, handleUpdate }) => (
    <div className="chart-options-content">
        <div className="chart-option-row">
            <span className="option-label">Chart Type</span>
            <SelectTypeAutocomplete
                name={'graphType'}
                handleSelect={(e) => handleUpdate('graphType', e.target.value)}
                defaultValue={selectedItem?.properties?.graphType || 'LINE'}
                list={selectedItem?.properties?.graphTypeEnums}
                renderValue={(i) => i?.name}
                uiType={'tableFilter'}
                noError={true}
                disableClearable={true}
            />
        </div>
        {selectedItem?.type !== attributeTypes.TARGET && (
            <div className="chart-option-row">
                <span className="option-label">Data Type</span>
                <SelectTypeAutocomplete
                    name={'defaultDateType'}
                    handleSelect={(e) => handleUpdate('defaultDateType', e.target.value)}
                    defaultValue={selectedItem?.defaultDateType || goalGraphTypeEnums.CUMULATIVE}
                    list={goalGraphDataTypeList}
                    renderValue={(i) => i?.name}
                    uiType={'tableFilter'}
                    noError={true}
                    disableClearable={true}
                />
            </div>
        )}
        {selectedItem?.type !== attributeTypes.TARGET &&
            (selectedItem?.defaultDateType || goalGraphTypeEnums.CUMULATIVE) === goalGraphTypeEnums.CUMULATIVE && (
                <ValueModeSwitch selectedItem={selectedItem} handleUpdate={handleUpdate} />
            )}
        {(selectedItem?.type === attributeTypes.TARGET || selectedItem?.defaultDateType === goalGraphTypeEnums.TARGET_OVERVIEW) && (
            <SessionGroupBySwitch selectedItem={selectedItem} handleUpdate={handleUpdate} />
        )}
    </div>
);

const CustomizeSection = ({ chartSettings, handleUpdate, chartType }) => (
    <div className="chart-options-content">
        <div className="flex-align-center chart-option-row" style={{ gap: 16, flexDirection: 'row' }}>
            <div className="color-picker-wrapper">
                <span className="option-label">Background</span>
                <div className="color-input-container">
                    <input
                        type="color"
                        defaultValue={chartSettings?.customize?.background || '#ffffff'}
                        ref={(el) => {
                            if (!el) return;
                            el.onchange = (e) =>
                                handleUpdate('customize', {
                                    ...chartSettings?.customize,
                                    background: e.target.value,
                                });
                        }}
                    />
                    <span>{chartSettings?.customize?.background || '#ffffff'}</span>
                </div>
            </div>
            <div className="color-picker-wrapper">
                <span className="option-label">Border</span>
                <div className="color-input-container">
                    <input
                        type="color"
                        defaultValue={chartSettings?.customize?.border || '#E4E7EC'}
                        ref={(el) => {
                            if (!el) return;
                            el.onchange = (e) =>
                                handleUpdate('customize', {
                                    ...chartSettings?.customize,
                                    border: e.target.value,
                                });
                        }}
                    />
                    <span>{chartSettings?.customize?.border || '#E4E7EC'}</span>
                </div>
            </div>
        </div>

        {chartType !== 'BAR' && (
            <div className="behavior-row" style={{ margin: '4px 0' }}>
                <span className="option-label">Smooth Lines</span>
                <label className="toggle-switch">
                    <input
                        type="checkbox"
                        checked={chartSettings?.customize?.smooth !== false}
                        onChange={(e) =>
                            handleUpdate('customize', {
                                ...chartSettings?.customize,
                                smooth: e.target.checked,
                            })
                        }
                    />
                    <span className="slider"></span>
                </label>
            </div>
        )}
        <div className="behavior-row">
            <span className="option-label">Plot Null Values</span>
            <label className="toggle-switch">
                <input
                    type="checkbox"
                    checked={!!chartSettings?.customize?.plotNull}
                    onChange={(e) =>
                        handleUpdate('customize', {
                            ...chartSettings?.customize,
                            plotNull: e.target.checked,
                        })
                    }
                />
                <span className="slider"></span>
            </label>
        </div>
    </div>
);

const titleTypes = [
    { name: 'Chart Title', id: 'chartTitle' },
    { name: 'Chart Subtitle', id: 'chartSubtitle' },
    { name: 'Horizontal Axis Title', id: 'bottomText' },
    { name: 'Vertical Axis Title', id: 'leftSideText' },
];

const TitlesSection = ({ chartSettings, handleUpdate }) => (
    <div className="chart-options-content">
        {titleTypes.map((type) => (
            <div className="chart-option-row" key={type.id}>
                <span className="option-label">{type.name}</span>
                <Input
                    value={chartSettings?.[type.id] || ''}
                    onChange={(e) => handleUpdate(type.id, e.target.value)}
                    placeholder={type.name}
                    disableUnderline
                    className="chart-input"
                />
            </div>
        ))}
    </div>
);

const DEFAULT_COLORS = ['#0C66E4', '#36B37E', '#FF5630', '#FFAB00', '#6554C0', '#00B8D9', '#FF8B00', '#8777D9'];

const SeriesPanel = ({ index, config, onChange, name, chartType, isFirst }) => {
    const [expanded, setExpanded] = useState(!!isFirst);
    const color = config?.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
    const thickness = config?.thickness || '2px';
    const style = config?.style || 'Solid';
    const points = config?.points || 'Circle';
    const visible = config?.visible !== false;

    const update = (field, value) => {
        onChange(index, { ...config, color, thickness, style, points, visible, [field]: value });
    };

    return (
        <div className="series-panel">
            <div className="chart-option-row" style={{ cursor: 'pointer', margin: '0 0 12px 0' }}>
                <div className="series-header">
                    <span className="series-dot" style={{ backgroundColor: color }}></span>
                    {/*<span className="option-label">{`Series ${index + 1}: ${name}`}</span>*/}
                    <span className="option-label" onClick={() => setExpanded(!expanded)} style={{ flex: 1, cursor: 'pointer' }}>
                        {name}
                    </span>
                    <label
                        className="toggle-switch-small"
                        style={{ marginLeft: 'auto', marginRight: 8 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <input type="checkbox" checked={visible} onChange={(e) => update('visible', e.target.checked)} />
                        <span className="slider"></span>
                    </label>
                    <img
                        src={Images.chevronDown}
                        alt="expand"
                        onClick={() => setExpanded(!expanded)}
                        style={{
                            width: 16,
                            height: 16,
                            transform: expanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s',
                            cursor: 'pointer',
                        }}
                    />
                </div>
            </div>
            {expanded && (
                <>
                    <div className="chart-option-row">
                        <div className="flex-align-center" style={{ gap: 16 }}>
                            <div className={`color-picker-wrapper ${chartType === 'BAR' ? 'full-width' : 'full-width'}`}>
                                <span className="option-label">Color</span>
                                <div className="color-input-container">
                                    <input
                                        type="color"
                                        defaultValue={color}
                                        ref={(el) => {
                                            if (!el) return;
                                            el.onchange = (e) => update('color', e.target.value);
                                        }}
                                    />
                                    <span>{color}</span>
                                </div>
                            </div>

                            {chartType !== 'BAR' && (
                                <div className="chart-option-row full-width">
                                    <span className="option-label">Thickness</span>
                                    <SelectTypeAutocomplete
                                        name={'thickness'}
                                        handleSelect={(e) => update('thickness', e.target.value)}
                                        defaultValue={thickness}
                                        list={[
                                            { name: '1px', id: '1px' },
                                            { name: '2px', id: '2px' },
                                            { name: '3px', id: '3px' },
                                        ]}
                                        renderValue={(i) => i?.name}
                                        uiType={'tableFilter'}
                                        noError={true}
                                        disableClearable={true}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {chartType !== 'BAR' && (
                        <div className="flex-align-center" style={{ gap: 16, marginTop: 12 }}>
                            <div className="chart-option-row full-width">
                                <span className="option-label">Style</span>
                                <SelectTypeAutocomplete
                                    name={'style'}
                                    handleSelect={(e) => update('style', e.target.value)}
                                    defaultValue={style}
                                    list={[
                                        { name: 'Solid', id: 'Solid' },
                                        { name: 'Dashed', id: 'Dashed' },
                                        { name: 'Dotted', id: 'Dotted' },
                                    ]}
                                    renderValue={(i) => i?.name}
                                    uiType={'tableFilter'}
                                    noError={true}
                                    disableClearable={true}
                                />
                            </div>
                            <div className="chart-option-row full-width">
                                <span className="option-label">Points</span>
                                <SelectTypeAutocomplete
                                    name={'points'}
                                    handleSelect={(e) => update('points', e.target.value)}
                                    defaultValue={points}
                                    list={[
                                        { name: 'Circle', id: 'Circle' },
                                        { name: 'Square', id: 'Square' },
                                        { name: 'Diamond', id: 'Diamond' },
                                        { name: 'None', id: 'None' },
                                    ]}
                                    renderValue={(i) => i?.name}
                                    uiType={'tableFilter'}
                                    noError={true}
                                    disableClearable={true}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const SeriesSection = ({ chartSettings, handleUpdate, selectedItem }) => {
    const { selectedGoalId, formList, activeSection } = useFormBuilder();
    const seriesConfig = chartSettings?.seriesConfig || [];
    const isTargetOverview = selectedItem?.defaultDateType === goalGraphTypeEnums.TARGET_OVERVIEW;

    // Determine series count and names dynamically
    const currentSectionIndex = (activeSection || 1) - 1;
    const currentSection = formList?.sections?.[currentSectionIndex];
    const selectedGoal = currentSection?.selectedGoals?.find((g) => g?.id === selectedGoalId);

    let seriesCount = 1;
    let seriesNames = [selectedGoal?.name || 'Line'];

    if (isTargetOverview) {
        // Look up actual targets from the selected goal
        const targets = selectedGoal?.targets || [];

        if (targets.length > 0) {
            seriesCount = targets.length;
            seriesNames = targets.map((t) => t?.name || 'Target');
        } else {
            seriesCount = 1;
            seriesNames = ['Target 1'];
        }
    }

    const handleSeriesChange = (index, newConfig) => {
        const updated = [...seriesConfig];
        updated[index] = newConfig;
        handleUpdate('seriesConfig', updated);
    };

    return (
        <div className="chart-options-content">
            {Array.from({ length: seriesCount }).map((_, i) => (
                <SeriesPanel
                    key={i}
                    index={i}
                    config={seriesConfig[i]}
                    onChange={handleSeriesChange}
                    name={seriesNames[i]}
                    chartType={selectedItem?.properties?.graphType}
                    isFirst={i === 0}
                />
            ))}
        </div>
    );
};

export const ChartBehaviorSection = ({ selectedItem, handleUpdate }) => {
    const [openSections, setOpenSections] = useState({
        setup: false,
        customize: false,
        titles: false,
        series: false,
    });

    const toggleSection = (section) => {
        setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const updateChartSettings = (key, value) => {
        const currentSettings = selectedItem?.properties?.chartSettings || {};
        const newSettings = { ...currentSettings, [key]: value };
        handleUpdate('chartSettings', newSettings);
    };

    const chartSettings = selectedItem?.properties?.chartSettings || {};

    return (
        <div className="properties-section">
            <div>
                <div className="section-header">
                    <img src={Images.behaivorIcon} alt="goal behavior" />
                    <span>GOAL BEHAVIOR</span>
                </div>

                {selectedItem?.defaultDateType !== goalGraphTypeEnums.TARGET_OVERVIEW && (
                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Show Mastery</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={!!selectedItem?.properties?.showMastery}
                                onChange={(e) => handleUpdate('showMastery', e.target.checked)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                )}
                <div className="behavior-row">
                    <div className="behavior-info">
                        <span className="behavior-title">Show Trend Line</span>
                    </div>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={!!selectedItem?.properties?.showTrendLine}
                            onChange={(e) => handleUpdate('showTrendLine', e.target.checked)}
                        />
                        <span className="slider"></span>
                    </label>
                </div>
                <div className="behavior-row">
                    <div className="behavior-info">
                        <span className="behavior-title">Show Average</span>
                    </div>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={!!selectedItem?.properties?.showAverageLine}
                            onChange={(e) => handleUpdate('showAverageLine', e.target.checked)}
                        />
                        <span className="slider"></span>
                    </label>
                </div>
                <div className="behavior-row">
                    <div className="behavior-info">
                        <span className="behavior-title">Show Goal Event</span>
                    </div>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={!!selectedItem?.properties?.showGoalEvent}
                            onChange={(e) => handleUpdate('showGoalEvent', e.target.checked)}
                        />
                        <span className="slider"></span>
                    </label>
                </div>
                <div className="behavior-row">
                    <div className="behavior-info">
                        <span className="behavior-title">Show Client Event</span>
                    </div>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={!!selectedItem?.properties?.showClientEvent}
                            onChange={(e) => handleUpdate('showClientEvent', e.target.checked)}
                        />
                        <span className="slider"></span>
                    </label>
                </div>
                {selectedItem?.type === attributeTypes.TARGET && (
                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Show Target Event</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={!!selectedItem?.properties?.showTargetEvent}
                                onChange={(e) => handleUpdate('showTargetEvent', e.target.checked)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                )}
                {selectedItem?.type === attributeTypes.TARGET && (
                    <div className="behavior-row">
                        <div className="behavior-info">
                            <span className="behavior-title">Show Target Baseline</span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={!!selectedItem?.properties?.showBaseline}
                                onChange={(e) => handleUpdate('showBaseline', e.target.checked)}
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                )}
            </div>

            <div className="properties-divider" style={{ margin: '24px 0' }} />

            <div className="section-header">
                <img src={Images.behaivorIcon} alt="chart" />
                <span>CHART BEHAVIOR</span>
            </div>

            <div className={`chart-accordion ${openSections.setup ? 'open' : ''}`}>
                <div className="chart-accordion-header" onClick={() => toggleSection('setup')}>
                    <img src={Images.editSetting} alt="setup" className="section-icon" />
                    <span>Setup</span>
                    <img src={Images.chevronDown} alt="toggle" className={`accordion-icon ${openSections.setup ? 'rotate' : ''}`} />
                </div>
                {openSections.setup && <SetupSection selectedItem={selectedItem} handleUpdate={handleUpdate} />}
            </div>

            <div className={`chart-accordion ${openSections.customize ? 'open' : ''}`}>
                <div className="chart-accordion-header" onClick={() => toggleSection('customize')}>
                    <img src={Images.customizeChart} alt="customize" className="section-icon" />
                    <span>Customize</span>
                    <img src={Images.chevronDown} alt="toggle" className={`accordion-icon ${openSections.customize ? 'rotate' : ''}`} />
                </div>
                {openSections.customize && (
                    <CustomizeSection
                        chartSettings={chartSettings}
                        handleUpdate={updateChartSettings}
                        chartType={selectedItem?.properties?.graphType}
                    />
                )}
            </div>

            <div className={`chart-accordion ${openSections.titles ? 'open' : ''}`}>
                <div className="chart-accordion-header" onClick={() => toggleSection('titles')}>
                    <img src={Images.chartTextBehavior} alt="titles" className="section-icon" />
                    <span>Titles</span>
                    <img src={Images.chevronDown} alt="toggle" className={`accordion-icon ${openSections.titles ? 'rotate' : ''}`} />
                </div>
                {openSections.titles && <TitlesSection chartSettings={chartSettings} handleUpdate={updateChartSettings} />}
            </div>

            <div className={`chart-accordion ${openSections.series ? 'open' : ''}`} style={{ borderBottom: 'none' }}>
                <div className="chart-accordion-header" onClick={() => toggleSection('series')}>
                    <img src={Images.chartSeries} alt="series" className="section-icon" />
                    <span>Series</span>
                    <img src={Images.chevronDown} alt="toggle" className={`accordion-icon ${openSections.series ? 'rotate' : ''}`} />
                </div>
                {openSections.series && (
                    <SeriesSection chartSettings={chartSettings} handleUpdate={updateChartSettings} selectedItem={selectedItem} />
                )}
            </div>
        </div>
    );
};
